/* eslint-disable max-len */
/* eslint-disable object-curly-newline */
import React, { useState } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment-timezone';
import TimeSelector from '../../components/timeSelect';

const toDisplay = (time) => (time ? moment(time, 'HH:mm:ss').format('h:mm A') : '');

/**
 * Inline editor for a member's secondary shift: a start/end pair and the
 * working weekdays on which it replaces the primary shift.
 */
function SecondaryScheduleBox({ id, startTime, endTime, days, workingDays, saveSchedule, open, onToggle }) {
  const [currentStartTime, setCurrentStartTime] = useState(toDisplay(startTime));
  const [currentEndTime, setCurrentEndTime] = useState(toDisplay(endTime));
  const [selectedDays, setSelectedDays] = useState(days || []);

  const hasSchedule = Boolean(startTime && endTime && days?.length);
  const summary = hasSchedule
    ? `${days.map((d) => d.slice(0, 3)).join(', ')} · ${toDisplay(startTime)} - ${toDisplay(endTime)}`
    : 'None';

  const toggleDay = (day) => setSelectedDays((cur) => (cur.includes(day) ? cur.filter((d) => d !== day) : [...cur, day]));

  const handleToggle = () => {
    // Reopening starts from what is saved, not a half-edited draft.
    if (!open) {
      setCurrentStartTime(toDisplay(startTime));
      setCurrentEndTime(toDisplay(endTime));
      setSelectedDays(days || []);
    }
    onToggle();
  };

  const handleSave = async () => {
    const saved = await saveSchedule({
      memberId: id,
      secondaryStartTime: moment(currentStartTime, 'h:mm A').format('HH:mm:ss'),
      secondaryEndTime: moment(currentEndTime, 'h:mm A').format('HH:mm:ss'),
      secondaryDays: workingDays.filter((d) => selectedDays.includes(d)),
    });
    if (saved) onToggle();
  };

  const handleRemove = async () => {
    const saved = await saveSchedule({ memberId: id, secondaryStartTime: null, secondaryEndTime: null, secondaryDays: [] });
    if (saved) onToggle();
  };

  let toggleLabel = hasSchedule ? 'Edit' : 'Add';
  if (open) toggleLabel = 'Cancel';

  const canSave = currentStartTime && currentEndTime && currentStartTime !== currentEndTime && selectedDays.length > 0;

  return (
    <div className="relative inline-flex items-center gap-2">
      <p className={`text-xs truncate ${hasSchedule ? 'text-ink-700' : 'text-ink-500'}`} style={{ margin: 0, maxWidth: 190 }} title={hasSchedule ? summary : undefined}>
        {summary}
      </p>
      <button
        type="button"
        onClick={handleToggle}
        className={`inline-flex items-center box-border px-2.5 py-1 rounded-[6px] text-xs font-medium border bg-white transition flex-shrink-0 ${open ? 'text-[#CC4525] border-[#f3cfc6] hover:bg-[#fdf6f4]' : 'text-ink-900 border-rule hover:border-brand-500 hover:text-brand-700'}`}
        style={{ borderStyle: 'solid' }}
      >
        {toggleLabel}
      </button>
      {open && (
        <div
          className="absolute z-10 bg-white rounded-[12px] border border-rule"
          style={{ top: 38, right: 0, width: 400, padding: 16, borderStyle: 'solid', boxShadow: '0 8px 24px rgba(14, 30, 64, 0.12)' }}
        >
          <h5 className="text-sm font-semibold text-ink-900 mb-1">Secondary Schedule</h5>
          <p className="text-xs text-ink-500 mb-3">Replaces the primary schedule on the selected days. Every other working day uses the primary schedule.</p>
          <div className="flex gap-3 mb-3">
            <div className="flex-1 min-w-0">
              <span className="block text-xs text-ink-500 mb-1">Starts from</span>
              <TimeSelector time={currentStartTime} setTime={setCurrentStartTime} />
            </div>
            <div className="flex-1 min-w-0">
              <span className="block text-xs text-ink-500 mb-1">Ends at</span>
              <TimeSelector time={currentEndTime} setTime={setCurrentEndTime} />
            </div>
          </div>
          <span className="block text-xs text-ink-500 mb-1">Applies on</span>
          <div className="flex flex-wrap gap-1.5 mb-4">
            {workingDays.map((day) => {
              const active = selectedDays.includes(day);
              return (
                <button
                  key={day}
                  type="button"
                  aria-pressed={active}
                  onClick={() => toggleDay(day)}
                  className={`inline-flex items-center justify-center box-border h-8 px-3 rounded-[6px] text-xs font-medium border transition ${active ? 'bg-brand-500 text-white border-brand-500' : 'bg-white text-ink-900 border-rule hover:border-brand-500'}`}
                  style={{ borderStyle: 'solid' }}
                >
                  {day.slice(0, 3)}
                </button>
              );
            })}
          </div>
          <div className="flex gap-2">
            {hasSchedule && (
              <button
                type="button"
                onClick={handleRemove}
                className="inline-flex items-center justify-center box-border h-10 px-4 bg-white text-[#CC4525] border border-[#f3cfc6] rounded-[8px] text-sm font-semibold hover:bg-[#fdf6f4] transition"
                style={{ borderStyle: 'solid' }}
              >
                Remove
              </button>
            )}
            <button
              type="button"
              onClick={handleSave}
              disabled={!canSave}
              className="inline-flex items-center justify-center box-border h-10 flex-1 px-4 bg-[#53A551] text-white rounded-[8px] text-sm font-semibold hover:bg-[#458a44] disabled:opacity-40 disabled:cursor-not-allowed transition"
            >
              Save
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

SecondaryScheduleBox.propTypes = {
  id: PropTypes.number.isRequired,
  startTime: PropTypes.string,
  endTime: PropTypes.string,
  days: PropTypes.arrayOf(PropTypes.string),
  workingDays: PropTypes.arrayOf(PropTypes.string).isRequired,
  saveSchedule: PropTypes.func.isRequired,
  open: PropTypes.bool,
  onToggle: PropTypes.func.isRequired,
};

SecondaryScheduleBox.defaultProps = { startTime: '', endTime: '', days: [], open: false };

export default SecondaryScheduleBox;
